import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import Loading from '../ui/Loading';

interface AgencySetupGuardProps {
  children: React.ReactNode;
}

const AgencySetupGuard: React.FC<AgencySetupGuardProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading, agencyInfo, checkAgencyCompletion } = useAuthStore();
  const [checking, setChecking] = useState(false);
  const isCheckingRef = useRef(false);
  const currentLang = i18n.language;

  useEffect(() => {
    const checkAndRedirect = async () => {
      // Prevent multiple simultaneous checks
      if (isCheckingRef.current) {
        return;
      }

      // Don't check if still loading auth or if we're already on the agency setup page
      if (loading || location.pathname.includes('/agency-setup')) {
        setChecking(false);
        return;
      }

      // Set checking to true only when we actually need to check
      setChecking(true);

      // If not an agency user, allow access
      if (!user || !profile || profile.role !== 'agency') {
        setChecking(false);
        return;
      }

      // If we already have agency info, allow access
      if (agencyInfo) {
        setChecking(false);
        return;
      }

      // Need to check agency completion
      try {
        isCheckingRef.current = true;
        
        // Add timeout to prevent hanging
        const checkPromise = checkAgencyCompletion();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Agency check timeout')), 3000)
        );
        
        const isComplete = await Promise.race([checkPromise, timeoutPromise]);
        
        if (!isComplete) {
          // Agency setup is incomplete, redirect to setup page
          navigate(`/${currentLang}/agency-setup`, { 
            replace: true,
            state: { 
              from: location.pathname,
              message: 'Please complete your agency setup to continue.' 
            }
          });
          return;
        }
      } catch (error) {
        console.error('AgencySetupGuard: Error checking agency setup:', error);
        // On error, allow access to prevent blocking
        if (error.message === 'Agency check timeout') {
          console.log('AgencySetupGuard: Timeout occurred, allowing access');
        }
      } finally {
        isCheckingRef.current = false;
      }
      
      setChecking(false);
    };

    checkAndRedirect();
    
    // Add a safety timeout to prevent infinite loading
    const safetyTimeout = setTimeout(() => {
      if (checking) {
        setChecking(false);
      }
    }, 3000); // Reduced from 6 to 3 seconds

    return () => {
      clearTimeout(safetyTimeout);
    };
  }, [user?.id, profile?.role, loading, agencyInfo, location.pathname, navigate, currentLang]);

  // Don't show loading here - let Dashboard handle it with sidebar/topbar structure
  // Just render children and let Dashboard show inline loading if needed
  return <>{children}</>;
};

export default AgencySetupGuard; 